import { useEffect, useState } from 'react';

import { useAuthContext } from '@/contexts/auth-context';

import { fetchAndStoreWxJwt } from '../utils/wx-jwt-token';

export type WxJwtStatus = 'pending' | 'ready' | 'unavailable';

/**
 * Fetches the WX JWT for a unified auth account and reports its status.
 * A non-unified auth account never gets one, so it reports `unavailable`.
 */
export function useWxJwtStatus(): WxJwtStatus {
  const { whoami } = useAuthContext();
  const [status, setStatus] = useState<WxJwtStatus>('pending');

  useEffect(() => {
    if (!whoami) {
      setStatus('pending');
      return;
    }

    if (!whoami.account_unified_auth) {
      setStatus('unavailable');
      return;
    }

    let cancelled = false;

    const fetchAndWaitForJwt = async (): Promise<void> => {
      try {
        const jwtPromise = fetchAndStoreWxJwt(whoami);
        const success = jwtPromise ? await jwtPromise : false;
        if (!cancelled) {
          setStatus(success ? 'ready' : 'unavailable');
        }
      } catch {
        if (!cancelled) {
          setStatus('unavailable');
        }
      }
    };

    void fetchAndWaitForJwt();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      setStatus('pending');
    };
  }, [whoami]);

  return status;
}

/**
 * @returns {boolean} Whether the WX JWT is stored and ready to use
 */
export function useWxJwtReady() {
  return useWxJwtStatus() === 'ready';
}
